"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { SignOutButton, useAuth, useUser } from "@clerk/nextjs";
import { cx } from "./cx";
import { ButtonLink } from "./button";
import { AccountMenu } from "./account-menu";
import { NavShell } from "./nav-shell";
import { FeedbackButton } from "./feedback-button";

/**
 * The pill-nav's primary links + the signed-in account menu. CLIENT component
 * (split from site-chrome so SiteChrome stays a server component).
 *
 * Auth resolution is CONFIG-GATED so it's correct on every surface without each
 * layout having to pass `signedIn` (which would force static/SEO pages dynamic):
 *   - Keyless (no `NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY`, i.e. build/tests/dev):
 *     `SiteNav` renders `StaticNav`, driven purely by the `signedIn` prop —
 *     exactly the pre-auth behavior, so `@clerk/nextjs` client hooks are never
 *     called without a provider.
 *   - Clerk configured: `SiteNav` renders `AuthNav`, which reads the real
 *     signed-in state from `useAuth()` on the client (falling back to the
 *     server-provided `signedIn` hint before Clerk loads, to avoid a flash) and
 *     shows the account menu with the user's identity. Because this resolves
 *     client-side, a static page can pass `signedIn={false}` and still show the
 *     signed-in nav after hydration — no layout wiring, no de-static-ing.
 *
 * The env is read at render (not module scope) so tests can toggle the path.
 */

function norm(p: string) {
  return p.length > 1 && p.endsWith("/") ? p.slice(0, -1) : p;
}

/** A primary nav link that lights green on the active path (parent lights for children). */
export function NavLink({ href, label }: { href: string; label: string }) {
  const current = norm(usePathname() ?? "/");
  const target = norm(href);
  const active = target === "/" ? current === "/" : current === target || current.startsWith(`${target}/`);
  return (
    <Link
      className={cx("pz-sitenav__link", active && "pz-sitenav__link--active")}
      href={href}
      aria-current={active ? "page" : undefined}
    >
      {label}
    </Link>
  );
}

/** The signed-out branch, shared by both nav paths. */
export function SignedOutLinks() {
  return (
    <>
      <NavLink href="/features/" label="Features" />
      <NavLink href="/pricing/" label="Pricing" />
      <NavLink href="/help/" label="Docs" />
      <NavLink href="/sign-in/" label="Sign in" />
      <ButtonLink href="/sign-up/" variant="primary" size="sm">
        Sign up
      </ButtonLink>
    </>
  );
}

/** Signed-in marketing links (the account control rides in NavShell's `trailing`). */
function SignedInMarketingLinks() {
  return (
    <>
      <NavLink href="/my-maps/" label="My maps" />
      <NavLink href="/pricing/" label="Pricing" />
      <NavLink href="/help/" label="Docs" />
    </>
  );
}

/** Clerk-configured path: real client auth state + the account menu. */
function AuthNav({ initialSignedIn }: { initialSignedIn: boolean }) {
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  // Before Clerk hydrates (and during SSR) fall back to the server hint so the
  // nav doesn't flash the wrong state on surfaces that already know it.
  const signedIn = isLoaded ? Boolean(isSignedIn) : initialSignedIn;

  if (!signedIn) {
    return (
      <NavShell className="pz-sitenav">
        <SignedOutLinks />
      </NavShell>
    );
  }

  return (
    <NavShell
      className="pz-sitenav"
      trailing={
        <div className="pz-navactions">
          <FeedbackButton />
          <AccountMenu name={user?.fullName ?? null} email={user?.primaryEmailAddress?.emailAddress ?? null} />
        </div>
      }
    >
      <SignedInMarketingLinks />
    </NavShell>
  );
}

/** Keyless path: prop-driven, no Clerk client hooks — the pre-auth behavior. */
function StaticNav({ signedIn }: { signedIn: boolean }) {
  return (
    <NavShell className="pz-sitenav">
      {signedIn ? (
        <>
          <FeedbackButton />
          {/* Plain active-highlightable links — the green marks the CURRENT page. */}
          <SignedInMarketingLinks />
          <NavLink href="/account/" label="Account" />
          <SignOutButton redirectUrl="/">
            <button type="button" className="pz-sitenav__link pz-sitenav__signout">
              Sign out
            </button>
          </SignOutButton>
        </>
      ) : (
        <SignedOutLinks />
      )}
    </NavShell>
  );
}

export function SiteNav({ signedIn, clientAuth = true }: { signedIn: boolean; clientAuth?: boolean }) {
  const clerkEnabled = Boolean(process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY);
  return clerkEnabled && clientAuth ? <AuthNav initialSignedIn={signedIn} /> : <StaticNav signedIn={signedIn} />;
}
